import React, { createContext, useContext, useEffect, useState } from "react";
import {
  RESTRICTED_TERRITORIES,
  GEO_API_PROVIDERS,
  RETRY_ATTEMPTS,
  RETRY_DELAY,
} from "../config/restrictions";
import { Loader2 } from "lucide-react";

interface GeoRestrictionContextType {
  isLoading: boolean;
  isRestricted: boolean;
  error: string | null;
}

const GeoRestrictionContext = createContext<GeoRestrictionContextType>({
  isLoading: true,
  isRestricted: false,
  error: null,
});

export const useGeoRestriction = () => useContext(GeoRestrictionContext);

interface GeoResponse {
  country_code?: string;
  countryCode?: string;
  error?: boolean;
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const GeoRestrictionProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [state, setState] = useState<GeoRestrictionContextType>({
    isLoading: true,
    isRestricted: false,
    error: null,
  });

  useEffect(() => {
    const fetchFromProvider = async (
      provider: (typeof GEO_API_PROVIDERS)[0],
      attempt: number = 0
    ): Promise<string | null> => {
      try {
        const url = provider.apiKey
          ? `${provider.url}?api_key=${provider.apiKey}`
          : provider.url;

        const response = await fetch(url);
        const data: GeoResponse = await response.json();

        if (data.error) {
          throw new Error("API error");
        }

        const countryCode = data[
          provider.countryCodePath as keyof GeoResponse
        ] as string;
        if (!countryCode) {
          throw new Error("No country code in response");
        }

        return countryCode;
      } catch (error) {
        if (attempt < RETRY_ATTEMPTS) {
          await delay(RETRY_DELAY * Math.pow(2, attempt));
          return fetchFromProvider(provider, attempt + 1);
        }
        return null;
      }
    };

    const checkGeoRestriction = async () => {
      // Try each provider in sequence
      for (const provider of GEO_API_PROVIDERS) {
        const countryCode = await fetchFromProvider(provider);
        if (countryCode) {
          setState({
            isLoading: false,
            isRestricted: RESTRICTED_TERRITORIES.includes(countryCode),
            error: null,
          });
          return;
        }
      }

      // All providers failed
      setState({
        isLoading: false,
        isRestricted: true, // Fail safe: restrict access if we can't determine location
        error:
          "Unable to verify your location. Access restricted for security.",
      });
    };

    checkGeoRestriction();
  }, []);

  if (state.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="">
          <h1 className="text-xl font-body font-semibold text-foreground flex items-center gap-2">
            Loading... <Loader2 className="h-4 w-4 animate-spin" />
          </h1>
        </div>
      </div>
    );
  }

  if (state.isRestricted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="rounded-2xl bg-card p-8 shadow-2xl max-w-md">
          <div className="flex flex-col items-center text-center">
            <img
              src="https://i.imgur.com/fhkEFLp.png"
              alt="Geo Restricted"
              className="w-20 h-20 mb-4"
            />
            <h2 className="text-xl font-body font-normal mb-4">
              We're sorry, but degencoinflip.com isn't available in your region
              due to gaming license regulations.
            </h2>
            {/* <p className="text-muted-foreground">
              {state.error || RESTRICTION_MESSAGE}
            </p> */}
            <small className="font-semibold text-muted-foreground">
              If you are using VPN, please disable it and try again.
            </small>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
